// TRADEFLOW: the two read-only trade action views, Front office and Back office.
//
// ONE COMPONENT, TWO DESKS, driven by the `desk` prop. The two views differ only in which endpoint
// they read and which actions appear, so two near-identical files would be two places to change.
//
// READ ONLY BY CONSTRUCTION: no form, no input, no button that writes, and no POST/PUT/PATCH/DELETE.
// A trade advances in Arfina and in the confirmation tool, never here. This screen reports.
//
// ACCESS IS ENFORCED SERVER SIDE, by membership of the configured recipient list plus coo/admin. This
// component renders whatever it is given and treats a 403 as "not for you" rather than an error,
// because for most accounts that is the correct and expected answer.
//
// NO EMAIL ADDRESS IS RENDERED: the endpoints do not return one.
//
// WHY NOT "ENTITY": in this codebase "entity" means an internal Enko legal company. `fund` is the
// Enko fund and `counterparty` is the external party, so neither borrows that word.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

const C = {
  navy: '#1a1a2e',
  muted: '#777',
  border: '#e8e8ef',
  warnBg: '#fff6e5',
  warnText: '#8a5a00',
  okBg: '#eef7ee',
  okText: '#2d6a2d',
  cardBg: '#ffffff',
  pageBg: '#f8f9fa',
};

const s = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: 1100,
               margin: '0 auto', padding: 24, backgroundColor: C.pageBg },
  h1: { fontSize: 24, fontWeight: 600, color: C.navy, margin: 0 },
  sub: { fontSize: 14, color: C.muted, margin: '4px 0 0 0' },
  card: { backgroundColor: C.cardBg, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: 20, marginTop: 24 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.muted, padding: '8px 10px',
        borderBottom: `1px solid ${C.border}`, textTransform: 'uppercase', letterSpacing: '0.03em' },
  td: { fontSize: 13, color: C.navy, padding: '10px', borderBottom: `1px solid ${C.border}`,
        verticalAlign: 'top' },
  meta: { fontSize: 11, color: C.muted },
  pill: (ok) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                   fontWeight: 600,
                   backgroundColor: ok ? C.okBg : C.warnBg,
                   color: ok ? C.okText : C.warnText }),
  empty: { fontSize: 13, color: C.muted, padding: '12px 0' },
  err: { fontSize: 13, color: '#b00020', padding: '12px 0' },
};

const DESKS = {
  front_office: {
    path: 'front-office',
    title: 'Front office trade actions',
    subtitle: 'Trades awaiting approval and signature. Read only: trades advance in Arfina.',
  },
  back_office: {
    path: 'back-office',
    title: 'Back office trade actions',
    subtitle: 'Trades awaiting confirmation, validation and sending back. Read only.',
  },
};

const show = (v) => (v && String(v).trim() ? v : '-');

// A timestamp is shown as given by the server (ISO), trimmed to minutes: the useful precision for
// "how long has this been waiting" without implying a precision the source does not have.
const shortTime = (iso) => {
  if (!iso) return '-';
  const t = String(iso).replace('T', ' ');
  return t.length > 16 ? t.slice(0, 16) : t;
};

export default function TradeActions({ desk = 'front_office' }) {
  const config = DESKS[desk] || DESKS.front_office;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setForbidden(false);
    setFailed(false);
    axios.get(`${API}/trades/${config.path}`)
      .then((res) => { if (live) setRows(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => {
        if (!live) return;
        // A 403 is an answer, not a fault: most accounts are not on either desk.
        if (err && err.response && err.response.status === 403) setForbidden(true);
        else setFailed(true);
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [config.path]);

  return (
    <div style={s.container}>
      <div>
        <h1 style={s.h1}>{config.title}</h1>
        <p style={s.sub}>{config.subtitle}</p>
      </div>

      <div style={s.card}>
        {loading && <div style={s.empty}>Loading...</div>}
        {forbidden && !loading && (
          <div style={s.empty}>
            This view is limited to the desk it belongs to.
          </div>
        )}
        {failed && !loading && (
          <div style={s.err}>Could not load the pending trade actions.</div>
        )}
        {!loading && !forbidden && !failed && rows.length === 0 && (
          <div style={s.empty}>Nothing pending.</div>
        )}
        {!loading && !forbidden && !failed && rows.length > 0 && (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Action</th>
                <th style={s.th}>Trade</th>
                <th style={s.th}>Fund</th>
                <th style={s.th}>Counterparty</th>
                <th style={s.th}>Pending since</th>
                <th style={s.th}>Reminder</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={s.td}>{show(r.action_label)}</td>
                  <td style={s.td}>{show(r.reference)}</td>
                  <td style={s.td}>{show(r.fund)}</td>
                  <td style={s.td}>{show(r.counterparty)}</td>
                  <td style={s.td}>{shortTime(r.pending_since)}</td>
                  <td style={s.td}>
                    {/* "Sent" and "not sent yet" are different situations for whoever is looking:
                        one means the desk has been told, the other that nobody has yet. */}
                    <span style={s.pill(r.reminder_sent)}>
                      {r.reminder_sent ? 'sent' : 'not sent'}
                    </span>
                    {r.reminder_sent && r.reminder_sent_at && (
                      <div style={s.meta}>{shortTime(r.reminder_sent_at)}</div>
                    )}
                    {!r.reminder_sent && r.reminder_due_at && (
                      <div style={s.meta}>due {shortTime(r.reminder_due_at)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
